/**
 * Captura da tela atual para anexar ao chamado de suporte.
 *
 * POR QUE UM WRAPPER: a escolha de biblioteca é discutível e provavelmente vai
 * mudar. `html2canvas` foi escolhido por ser o único caminho de fato
 * automático — `getDisplayMedia` tem fidelidade melhor, inclusive iframe, mas
 * exige que o usuário escolha e autorize a tela a cada uso, o que mata a
 * conveniência que a funcionalidade existe para dar. Todo o resto do código
 * fala com este módulo, não com a lib.
 *
 * LIMITAÇÕES CONHECIDAS (aceitas ao optar por captura automática):
 *   - não captura `<iframe>` nem `<video>` (aparecem em branco);
 *   - reconstrói o layout a partir do CSS computado, então sombras, filtros e
 *     fontes exóticas podem sair diferentes do que está na tela;
 *   - imagem de outro domínio sem CORS suja o canvas e a captura falha.
 *
 * O import é DINÂMICO de propósito: são ~50 KB gzip que só interessam a quem
 * abre um chamado. Carregar no bundle principal penalizaria todo mundo por uma
 * ação rara.
 */

/** Largura máxima do PNG. Acima disso a imagem cresce sem ganhar informação. */
const LARGURA_MAX = 1600;

/**
 * A ESCOLHA/VALIDAÇÃO de imagem mudou de casa: foi para `@pps/ui/lib/imagem`,
 * porque o admin passou a anexar imagem também (F3) e a lista de tipos aceitos
 * espelha o `allowed_mime_types` do bucket — duas cópias dela divergiriam.
 *
 * O reexport mantém os imports existentes deste módulo funcionando: aqui
 * continua sendo o lugar de quem fala de "print da tela", e a captura
 * automática (html2canvas) segue morando só neste app.
 *
 * `CapturaTela` é apelido de `ImagemEscolhida` — o mesmo formato, com o nome
 * que o CRM já usa em dezenas de lugares.
 */
export {
  ACCEPT_IMAGEM,
  MIMES_ACEITOS,
  TAMANHO_MAX_BYTES,
  imagemDoArquivo,
} from '@pps/ui/lib/imagem';
export type {
  ErroImagem,
  ImagemEscolhida as CapturaTela,
  ResultadoImagem,
} from '@pps/ui/lib/imagem';

import type { ImagemEscolhida } from '@pps/ui/lib/imagem';

/**
 * Captura o `<body>` inteiro. Devolve `null` quando a captura falha — abrir
 * chamado NUNCA pode quebrar por causa do print: o anexo é acessório, o relato
 * é o que importa.
 */
export async function capturarTela(): Promise<ImagemEscolhida | null> {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;

  try {
    const { default: html2canvas } = await import('html2canvas');

    const canvas = await html2canvas(document.body, {
      // O padrão captura a página inteira, inclusive o que está fora da
      // viewport. Queremos o que o usuário está vendo — é disso que ele fala.
      x: window.scrollX,
      y: window.scrollY,
      width: window.innerWidth,
      height: window.innerHeight,
      scale: Math.min(window.devicePixelRatio || 1, 2),
      useCORS: true,
      // Sem isto, imagem de outro domínio sem CORS derruba a captura inteira.
      allowTaint: false,
      logging: false,
      backgroundColor: null,
      // O diálogo de abertura já está montado quando capturamos; sem esta
      // exclusão o print seria uma foto do próprio formulário.
      ignoreElements: (el) => el.hasAttribute?.('data-screenshot-ignore') === true,
    });

    const escala = canvas.width > LARGURA_MAX ? LARGURA_MAX / canvas.width : 1;
    const alvo = escala === 1 ? canvas : redimensionar(canvas, escala);

    const blob = await new Promise<Blob | null>((resolve) =>
      alvo.toBlob(resolve, 'image/png'),
    );
    if (!blob || blob.size > TAMANHO_MAX_BYTES) return null;

    return {
      blob,
      previewUrl: URL.createObjectURL(blob),
      largura: alvo.width,
      altura: alvo.height,
      mime: 'image/png',
    };
  } catch {
    // Silencioso por decisão: o usuário está reportando um problema, não quer
    // receber um segundo erro por causa da captura.
    return null;
  }
}

function redimensionar(origem: HTMLCanvasElement, escala: number): HTMLCanvasElement {
  const destino = document.createElement('canvas');
  destino.width = Math.round(origem.width * escala);
  destino.height = Math.round(origem.height * escala);
  const ctx = destino.getContext('2d');
  if (ctx) {
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(origem, 0, 0, destino.width, destino.height);
  }
  return destino;
}

/**
 * Contexto do browser que vai junto do chamado. Barato de coletar e poupa a
 * ida-e-volta de "qual navegador você está usando?".
 */
/**
 * O retorno é estreito de propósito (`string | number`, não `unknown`): a
 * coluna `contexto` é `jsonb`, e o tipo gerado do Supabase exige `Json`.
 * `Record<string, unknown>` não é atribuível a `Json` — o compilador não tem
 * como saber que tudo aqui é serializável.
 */
export function coletarContexto(): Record<string, string | number> {
  if (typeof window === 'undefined') return {};
  return {
    user_agent: navigator.userAgent,
    idioma: navigator.language,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    tela: `${window.screen?.width}x${window.screen?.height}`,
    dpr: window.devicePixelRatio,
    capturado_em: new Date().toISOString(),
  };
}
